import React from "react";
import PropTypes from "prop-types";
import { reisy, use } from "reisy";
import Link from "./link.js";
import Image from "./image.js";
import { WrappingLoader } from "./inline-loader.js";
import { injectIntl, intlShape, defineMessages } from "react-intl";

const messages = defineMessages({
	heading: {
		id: "related.heading",
		defaultMessage: "Related stories:",
	},
});

export const styles = reisy`
@namespace Related;

Related {
	width: 100%;
	margin: 1em auto;
	padding: 24px;
	box-sizing: border-box;

	@media print {
		display: none;
	}
}

heading {
	width: 100%;
	margin-bottom: .75em;
	font: 1em $Fonts.aside;

	$Media.ltMedium {
		max-width: 400px;
		margin: 0 auto .5em;
	}
}

stories {
	display: flex;
	flex-wrap: wrap;
	$Media.ltMedium {
		flex-direction: column;
		align-items: center;
	}
}

RelatedStory {
	display: flex;
	flex: 1 0 33%;
	box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
	text-decoration: none;
	color: $Colors.TextDark;
	margin-bottom: 1em;
	max-width: 33%;

	$Media.ltMedium {
		max-width: 400px;
		width: 100%;
		flex-shrink: 1;
	}

	$Media.medium {
		&:last-child {
			margin-right: 0;
		}
	}
}

image {
	position: relative;
	width: 100px;
	height: 100px;
	flex: 0 0 100px;
	font-size: .75em;
}

info {
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 0 1.25em .5em .5em;
	flex: 1 0 0px;
	overflow-wrap: break-word;
	word-wrap: break-word;
	word-break: break-word;
	hyphens: auto;
}

title {
	font: 1.2em $Fonts.title;
	line-height: 1em;
}
subtitle {
	font: .9em $Fonts.subtitle;
}
description {
	font: .85em $Fonts.text;
}
`;

class RelatedStory extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
		};
	}

	componentDidUpdate(prevProps) {
		if (this.state.loading && this.props.story.id !== prevProps.story.id) {
			this.setState({ loading: false });
		}
	}

	onClick = (e) => {
		this.setState({ loading: true });
	};

	render() {
		const { story } = this.props;
		const { url } = this.context.player;

		let hierarchy = [story.lookupId];
		const collections = story.collections.map((c) => c.lookupId);

		if (collections.length) {
			if (url.hierarchy.length > 1) {
				const parents = url.hierarchy.slice(0, -1);

				while (parents.length) {
					const p = parents.pop();
					if (!!~collections.indexOf(p)) {
						hierarchy.unshift(p);
					} else if (hierarchy.length > 1) {
						break;
					}
				}
			}
			if (hierarchy.length === 1) {
				hierarchy.unshift(collections[0]);
			}
		}

		let href = null;

		switch (story.type) {
			case "link":
				href = story.data ? story.data.href : null;
				break;
			default:
				href = url.create({
					hierarchy,
					slug: story.slug,
					query: url.query.tags ? { tags: url.query.tags } : null,
				});
				break;
		}

		return (
			<Link
				to={href}
				title={`${story.title || ""}${
					story.subTitle ? ` – ${story.subTitle}` : ""
				}`}
				{...use(styles.RelatedStory)}
				onClick={this.onClick}
			>
				<div {...use(styles.image)}>
					<WrappingLoader loading={this.state.loading}>
						{story.image && (
							<Image
								image={{ variants: story.image }}
								sizes={"100px"}
								availWidth={100}
								ratio={1}
								absolute
								cover
								lazy
								placeholder
							/>
						)}
					</WrappingLoader>
				</div>
				<div {...use(styles.info)}>
					<div {...use(styles.title)}>{story.title}</div>
					{story.subTitle && (
						<div {...use(styles.subtitle)}>{story.subTitle}</div>
					)}
					{story.description && (
						<div {...use(styles.description)}>{story.description}</div>
					)}
				</div>
			</Link>
		);
	}
}
RelatedStory.contextTypes = {
	player: PropTypes.object,
};

class Related extends React.Component {
	constructor() {
		super();
		this.state = {
			related: false,
		};
	}
	componentDidUpdate(prevProps) {
		if (prevProps.story.id !== this.props.story.id) {
			this._loadRelated();
		}
	}
	componentDidMount() {
		this._loadRelated();
	}
	_loadRelated() {
		const { player } = this.context;
		player.api
			.loadRelated(this.props.story, this.props.count)
			.then((related) => this.setState({ related }));
	}
	render() {
		const { pages } = this.props.story;
		const { related } = this.state;
		const { intl } = this.context;

		return related && related.count ? (
			<div
				{...use(styles.Related, {
					maxWidth: `${pages[pages.length - 1].maxWidth}px`,
				})}
			>
				<h4 {...use(styles.heading)}>{intl.formatMessage(messages.heading)}</h4>
				<div {...use(styles.stories)}>
					{related.results.map((story) => (
						<RelatedStory key={story.id} story={story} />
					))}
				</div>
			</div>
		) : null;
	}
}
Related.contextTypes = {
	intl: intlShape,
	player: PropTypes.object,
};

export default Related;



// WEBPACK FOOTER //
// ./lib/components/related.js